import { useDispatch } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  CLIENTS_CONTACTS_ADMIN_ACCESS,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_CONTACTS,
  ROUTE_CLIENTS_NEW_CONTACTS,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { useLocation, useNavigate, useParams } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useEffect, useState } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { useClientContacts } from "../hooks/useClientContacts";
import { ClientContact } from "../types/clients-module-types";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import {
  setContactsFilterText,
  setIsShowingActives,
} from "../redux/reducers/clientContacts.reducer";
import { removeDiacritics } from "../../UsersModule/utils/userUtils";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientContactsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    ClientForHeaderInfo,
    isLoadingContacts,
    clientContactsInfo,
    userRolesList,
    rolesLoaded,
    isShowingActives,
    contactsFilterText,
  } = useClientContacts(Number(id));

  useEffect(() => {
    document.title = "The Hub - Client Contacts";

    dispatch(setActiveTab(ROUTE_CLIENTS_CONTACTS));

    let posY = localStorage.getItem("contactScrollY");
    if (posY)
      setTimeout(() => {
        window.scrollTo({
          top: Number(posY),
          behavior: "smooth",
        });
      }, 300);

    return () => {
      resetScrollY(globalThis.location.pathname);
      if (
        !(
          location.pathname.endsWith("/contactProfile") ||
          location.pathname.endsWith("/contacts") ||
          location.pathname.includes("newContact")
        )
      ) {
        setSeeActiveContacts(true);
        dispatch(setIsShowingActives(true));
        dispatch(setContactsFilterText(""));
      }
    };
  }, []);

  const [searchText, setSearchText] = useState(contactsFilterText);
  const [seeActiveContacts, setSeeActiveContacts] = useState(isShowingActives);

  let contactListToShow: Array<ClientContact> = clientContactsInfo
    .filter((contact: ClientContact) => {
      return seeActiveContacts ? contact.isActive : !contact.isActive;
    })
    .filter((contact: ClientContact) => {
      const lowerSearch = removeDiacritics(searchText).toLocaleLowerCase();
      return (
        removeDiacritics(contact.firstName)
          .toLocaleLowerCase()
          .includes(lowerSearch) ||
        removeDiacritics(contact.lastName)
          .toLocaleLowerCase()
          .includes(lowerSearch) ||
        removeDiacritics(contact.jobTitle)
          .toLocaleLowerCase()
          .includes(lowerSearch)
      );
    });

  const search = (value: string) => {
    setSearchText(value);
    dispatch(setContactsFilterText(value));
  };

  const onToggleButton = (state: boolean) => {
    setSeeActiveContacts(state);
    dispatch(setIsShowingActives(!isShowingActives));
  };

  const addContact = () => {
    const route = ROUTE_CLIENTS_NEW_CONTACTS.replace(
      ":id",
      id ? id.toString() : "0"
    );
    navigate(route, {
      state: {
        from:
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            : "",
      },
    });
  };

  const goToProfile = (contact: ClientContact) => {
    localStorage.setItem("contactScrollY", window.scrollY.toString());
    navigate(`/clients/${id}/contacts/${contact.id}/contactProfile`, {
      state: {
        from:
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            : "",
      },
    });
  };

  return (
    <div className="content">
      <HeaderClientsModule
        backFromRelationshipPage={
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
        }
      />
      {(isLoadingContacts || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isLoadingContacts &&
        rolesLoaded &&
        !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {!isLoadingContacts &&
        rolesLoaded &&
        verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <>
            <div className="d-flex my-3 justify-content-between">
              <div
                className="flex-column col-sm-6"
                style={{ marginTop: "3px" }}
              >
                <label htmlFor="contacts-search" className="col-form-label">
                  Search
                </label>
                <input
                  id="contacts-search"
                  type="text"
                  className="form-control form-control-sm common-input"
                  placeholder="Search by first name, last name, or Title"
                  value={searchText}
                  onChange={(event) => search(event.target.value)}
                />
              </div>

              <fieldset
                className="flex-column col-sm-2 ms-5"
                style={{ marginTop: "3px" }}
              >
                <div className="col-form-label">Contact Status</div>
                <div className="btn-group w-100">
                  <button
                    type="button"
                    onClick={() => onToggleButton(true)}
                    className={`btn btn-group-custom ${
                      !seeActiveContacts ? "btn-light" : "btn-active"
                    }`}
                    disabled={seeActiveContacts || isLoadingContacts}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggleButton(false)}
                    className={`btn btn-group-custom ${
                      !seeActiveContacts ? "btn-inactive" : "btn-light"
                    }`}
                    disabled={!seeActiveContacts || isLoadingContacts}
                  >
                    Inactive
                  </button>
                </div>
              </fieldset>

              {ClientForHeaderInfo?.isActive &&
                verifyModuleAccess(
                  true,
                  CLIENTS_CONTACTS_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <div className="flex-column" style={{ marginTop: "auto" }}>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => addContact()}
                    >
                      Add New Contact
                    </button>
                  </div>
                )}
            </div>

            {!isLoadingContacts && contactListToShow.length === 0 && (
              <div className="py-5">
                <NoContent
                  title={`No ${
                    isShowingActives ? "Active" : "Inactive"
                  } Contacts for this Client`}
                  text="Edit your filters to find the contacts you are looking for."
                  image={image}
                  style={{ margin: "0 auto" }}
                />
              </div>
            )}

            {!isLoadingContacts && contactListToShow.length > 0 && (
              <table
                className="table grey-table-header-background mt-4 mb-7"
                style={{ width: "100%" }}
              >
                <thead className="align-middle sticky sticky-header">
                  <tr>
                    <th
                      className="border-dark border-top fw-600 ps-5 column-25 pointer"
                      id="name"
                    >
                      Name
                    </th>
                    <th
                      className="border-dark border-top fw-600 ps-5 column-55 pointer"
                      id="title"
                    >
                      Title
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contactListToShow
                    .toSorted((a: ClientContact, b: ClientContact) => {
                      const lastNameCompare = a.lastName.localeCompare(
                        b.lastName
                      );
                      if (lastNameCompare !== 0) return lastNameCompare;
                      return a.firstName.localeCompare(b.firstName);
                    })
                    .map((contact: ClientContact) => {
                      return (
                        <tr
                          key={contact.id}
                          onClick={() => goToProfile(contact)}
                          className="client-contant-row"
                        >
                          <td className="ps-5 fw-500 client-contant-name">
                            {contact.lastName}, {contact.firstName}
                          </td>
                          <td className="ps-5 fw-500">{contact.jobTitle}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </>
        )}
      <Footer />
    </div>
  );
};
